import { useEffect, useState } from 'react';

import { Modal } from '../../components';
import { useQltClassNoteStore } from '../../stores/qlt';
import { CLASS_MARK_BANDS, bandById, bandIdOf, commentPayload, commentPresetsFor, hydrateComment, tagsForBand } from './classNoteFields';
import '../../styles/class-management.css';

const NOTE_LIMIT = 200;

export { CLASS_COMMENT_PRESETS, CLASS_MARK_BANDS, classMarkLabel } from './classNoteFields';
export type { ClassMarkBand } from './classNoteFields';

export interface ClassLogbookEditorProps {
  open: boolean;
  periodLabel: string;
  subjectTitle?: string;
  lessonName?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ClassLogbookEditor({ open, periodLabel, subjectTitle, lessonName, onClose, onSaved }: ClassLogbookEditorProps) {
  const note = useQltClassNoteStore((state) => state.currentNote);
  const loading = useQltClassNoteStore((state) => state.loading);
  const error = useQltClassNoteStore((state) => state.error);
  const saveData = useQltClassNoteStore((state) => state.saveData);
  const [band, setBand] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [extraNote, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    const hydrated = hydrateComment(note?.data);
    setBand(bandIdOf(note?.data));
    setTags(hydrated.tags);
    setNote(hydrated.note);
  }, [note, open]);

  function pickBand(next: string) {
    setBand(next);
    setTags((current) => tagsForBand(current, next));
  }

  function toggleTag(value: string) {
    setTags((current) => (current.indexOf(value) >= 0 ? current.filter((item) => item !== value) : current.concat(value)));
  }

  async function save() {
    const chosen = bandById(band);
    const updated = await saveData({ classMark: chosen ? chosen.mark : undefined, classMarkBand: band, ...commentPayload(tags, extraNote) });
    if (updated) {
      onSaved();
      onClose();
    }
  }

  const presets = commentPresetsFor(band);
  // a stored tag outside the band's list stays visible until the teacher changes the band
  const options = presets.concat(tags.filter((tag) => presets.indexOf(tag) < 0));
  const description = `${periodLabel} • Môn ${subjectTitle || 'học'}${lessonName ? `  |  Nội dung:  ${lessonName}` : ''}`;
  const actions = <button type="button" className="cm-btn-primary" disabled={loading || !note} onClick={() => void save()}>{loading ? 'Đang lưu…' : 'Xác nhận ghi sổ'}</button>;

  return (
    <Modal open={open} title="Đánh giá tiết học" description={description} className="cm-modal cm-modal--meta" actions={actions} onClose={onClose}>
      <div className="cm-label">Đánh giá lớp</div>
      <div className="cm-eval-grid" role="radiogroup" aria-label="Đánh giá lớp">
        {CLASS_MARK_BANDS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={band === item.id}
            className={`cm-eval-chip cm-eval-chip--${item.id}${band === item.id ? ' is-on' : ''}`}
            onClick={() => pickBand(band === item.id ? '' : item.id)}
          >
            <span className="cm-eval-chip__box" aria-hidden={true}>✓</span>
            <span className="cm-eval-chip__badge">{item.badge}</span>
            <span className="cm-eval-chip__label">{item.label}</span>
          </button>
        ))}
      </div>
      <div className="cm-label">Nhận xét lớp</div>
      <div className="cm-checklist" role="group" aria-label="Nhận xét lớp">
        {options.map((item) => (
          <label className={tags.indexOf(item) >= 0 ? 'cm-check is-on' : 'cm-check'} key={item}>
            <input type="checkbox" checked={tags.indexOf(item) >= 0} onChange={() => toggleTag(item)} />
            <span>{item}</span>
          </label>
        ))}
      </div>
      <div className="cm-label">Ghi chú thêm</div>
      <div className="cm-note">
        <textarea value={extraNote} maxLength={NOTE_LIMIT} onChange={(event) => setNote(event.target.value)} placeholder="Nhập nội dung ghi chú..." aria-label="Ghi chú thêm" />
        <span className="cm-note__count">{extraNote.length}/{NOTE_LIMIT}</span>
      </div>
      {error ? <p className="cm-error" role="alert">{error}</p> : null}
    </Modal>
  );
}
